import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Select, Tooltip, Icon, Divider, Spin, message } from 'antd';
import { fetchAddresses, getAddress } from 'src/api/organisationProfiling';
import { Address } from '../../../Library/Types';
import { CargoOperationType, LocationType } from '../../../Library/Constants';
import EditConsignorOrgModal from '../../pages/Organisation/Modals/EditConsignorOrgModal';
import  AddLocationModal from '../../../components/pages/Enquiry/Modals/AddLocationModal';


interface SelectAddressProps {
  value?: any;
  onChange?: (value: any, option: any) => void;
  placeholder?: string; 
  disabled?: boolean;
  cargoOperationType: CargoOperationType;
  addresses?: Address[];
  style?: React.CSSProperties;
  className?: string;
  allowEdit?: boolean;
  detailed?: boolean;
  locationType?: LocationType;
  labelInValue?: boolean;
  shipperId?: string;
}

const styles: { [key: string]: React.CSSProperties } = {
  addressLabel: {
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  cfsLocationBadge: {
    border: '1px solid #FFC83C',
    padding: '2px 6px',
    borderRadius: '6px',
    backgroundColor: '#FFFBD0',
    fontSize: '8px',
    lineHeight: '12px',
    display: 'inline-flex',
    alignItems: 'center',
  },
  factoryLocationBadge: {
    border: '1px solid #52C41A',
    padding: '2px 6px',
    borderRadius: '6px',
    backgroundColor: '#E6FFED',
    fontSize: '8px',
    lineHeight: '12px',
    display: 'inline-flex',
    alignItems: 'center',
  },
}

const SelectAddress: React.FC<SelectAddressProps> = ({
  value,
  onChange,
  placeholder = "Select Address",
  disabled = false,
  cargoOperationType,
  addresses: propsAddresses,
  style,
  className,
  allowEdit = false,
  detailed,
  locationType,
  labelInValue = false,
  shipperId
}) => {
  const [addresses, setAddresses] = useState(propsAddresses || []);
  const [loading, setLoading] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isLocationModalVisible, setIsLocationModalVisible] = useState(false);
  const apiTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const getAddressById = useMemo(
    () => new Map(addresses.map(a => [a.id, a])),
    [addresses]
  );

  const fetchAddressById = async (id: string) => {
    const organisationId = window.localStorage.getItem('organisationId');
    const stakeholderId = organisationId === shipperId ? undefined : shipperId;
    const response = await getAddress(id, stakeholderId);
    if (response.isSuccess){
      setAddresses(prev => {
        const isAlreadyInList = prev.some(addr => addr.id === response.data?.id);
        return isAlreadyInList ? prev : [response.data, ...prev];
      });
    }
  }

  const loadAddresses = async (queryString?: string, selectedAddressId?: string) => {
    if (apiTimeoutRef.current){
      clearTimeout(apiTimeoutRef.current)
      apiTimeoutRef.current = null
    }

    apiTimeoutRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        let payload: any = {
          addressType: locationType === LocationType.CFS ?  CargoOperationType.DESTUFFING : cargoOperationType,
          queryString, 
          shipperId  
        }
        if (locationType === LocationType.CFS){
          payload = {
            ...payload,
            locationList: [LocationType.CFS]
          }
        }
        const response = await fetchAddresses(payload);
        if (response?.isSuccess) {
          const selectedAddress = getAddressById.get(selectedAddressId ?? '')
          let addresses: Address[] = response.data ?? []
          const alreadyInList = addresses.some((addr) => addr.id === selectedAddress?.id )
          if (selectedAddress && !alreadyInList){
            addresses.unshift(selectedAddress)
          } 
          
          setAddresses(addresses)
          
        }
      } catch (error) {
        message.error("Failed to load addresses. Please try again later.");

      } finally {
        setLoading(false);
        apiTimeoutRef.current = null;
      }
    }, 300)

  };

  useEffect(() => {
    if (propsAddresses !== undefined) {
      setAddresses(propsAddresses);
    }
    loadAddresses();
  }, [cargoOperationType, propsAddresses]);

  useEffect(() => {
    return () => {
      if (apiTimeoutRef.current) {
        clearTimeout(apiTimeoutRef.current);
        apiTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (value?.key) {
      const isAlreadyInList = addresses.some(addr => addr.id === value?.key);
      if (!isAlreadyInList) fetchAddressById(value?.key);
    }
  }, [value]);

  const handleEditClick = (e: React.MouseEvent, address: Address) => {
    e.stopPropagation();
    setEditingAddress(address);
    setIsEditModalVisible(true);
  };

  const handleEditCancel = () => {
    setIsEditModalVisible(false);
    setEditingAddress(null);
  };

  const handleAddNewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsLocationModalVisible(true);

  };

  const handleEditSave = async (updatedAddress: Address) => {
    if (!propsAddresses) {
      await loadAddresses();
    }
    setIsEditModalVisible(false);
    setEditingAddress(null);
  };

  const renderAddNewMenu = (menu: React.ReactNode) => {
      return (
        <div>
          {menu}
          <Divider style={{ margin: '2px 0' }} />
          <button
            type="button"
            style={{ 
              padding: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              color: '#1890FF',
              border: 'none',
              background: 'transparent',
              width: '100%',
              textAlign: 'left'
            }}
            onMouseDown={handleAddNewClick}
            onClick={handleAddNewClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleAddNewClick(e as any);
              }
            }}
          >
            <Icon type="plus" style={{ marginRight: '8px' }} />
            <span>Add New</span>
          </button>
        </div>
      );
    };
  const renderLocationTypeBadge = (item: Address) => (
    item.location ? (
      <span style={item.location === LocationType.CFS ? styles.cfsLocationBadge : styles.factoryLocationBadge}>
        {LocationType[item.location]}
      </span>
    ) : null
  );
  
  const renderViewEditAddress = (item: Address) => (
    allowEdit ? (
      <Icon 
        type="ellipsis" 
        onClick={(e) => handleEditClick(e, item)}
        style={{ marginLeft: 8, cursor: 'pointer', color: '#1890ff' }}
      />
    ) : null
  );

  return (
    <>
      <Select
        showSearch
        allowClear
        value={value}
        onChange={onChange}
        style={{ width: '100%', ...style }}
        className={className}
        placeholder={placeholder}
        disabled={disabled}
        labelInValue = {labelInValue}
        onSelect={(option) => {
          loadAddresses('', option.key)
        }}
        onSearch={(val) => loadAddresses(val)}
        optionLabelProp="data-label"
        optionFilterProp="data-address"
        filterOption = {false}
        loading={loading}
        dropdownRender={(menu) => {
          if (loading){
            return <Spin style={{ margin: '20px 0 20px 45%' }} />
          }
          if (!allowEdit){
            return menu
          } 
          return renderAddNewMenu(menu)
        }}
        notFoundContent={loading ? <Spin size="small" /> : null}
      > 
        {addresses.map(item => (
          <Select.Option 
            key={item.id}
            value={item.id}
            data-label={`${item.name}, ${item.pincode ?? ''} (${item.referenceNumber})`}
            data-address={item.displayAddress}
            {...item}
          >
            {detailed ? 
              (<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, marginRight: '8px'}}>
                  <Tooltip title={item.displayAddress}>
                    <span style={styles.addressLabel}> 
                      {`${item.name} (${item.referenceNumber})`}</span>
                  </Tooltip>
                  <span style={{ fontSize: '12px', color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                    {[item?.address, item.landmark, item.city, item.state, item.pincode, item.countryName]
                      .filter((val) => {
                        return val;
                      })
                      .join(', ')}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0}}>
                  {renderLocationTypeBadge(item)}
                  {renderViewEditAddress(item)}
                </div>
              </div>)
             :
             (<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ flex: 1, minWidth: 0, marginRight: '8px' }}>
                <Tooltip title={item.displayAddress}>
                  <span style={styles.addressLabel}>
                    {`${item.name}, ${item.pincode ?? ''} (${item.referenceNumber})`}
                  </span>
                </Tooltip>
              </div>
              {renderViewEditAddress(item)}
            </div>)}
          </Select.Option>
        ))}
      </Select>

      {allowEdit && isEditModalVisible && (
        <EditConsignorOrgModal
          visible={isEditModalVisible}
          data={editingAddress || {}}
          closeEdit={handleEditCancel}
          refreshAddresses={handleEditSave}
          isModal={true}
        />
      )}

    {isLocationModalVisible && (
      <AddLocationModal
        visible={isLocationModalVisible}
        handleCancel={ () => {setIsLocationModalVisible(false)}}
        loadAddresses={loadAddresses}
        showLocationInput={true}
      />)}
    </>
  );
  
};

export default SelectAddress;